import { useNavigate, NavigateOptions, To, useParams } from 'react-router';
import { useCallback, startTransition, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { getCleanOrigin } from '../../utils/url-utils';

/**
 * React Router v7 + AutoI18nLayout 호환 i18n 네비게이션 훅
 * 
 * 🌟 핵심 기능:
 * - 현재 언어를 자동 감지 (URL params에서)
 * - navigate('/path') → navigate('/ko/path') 자동 변환
 * - AutoI18nLayout과 완벽 호환
 * - 외부 링크, 숫자 네비게이션 안전 처리
 * 
 * @example
 * const navigate = useI18nNavigate();
 * navigate('/feed'); // 자동으로 /ko/feed로 변환
 * navigate('/settings'); // 자동으로 /ko/settings로 변환
 * navigate(-1); // 뒤로가기 (언어 prefix 없이)
 */
export function useI18nNavigate() {
  const baseNavigate = useNavigate();
  const params = useParams();
  
  // 현재 언어 추출 (URL params에서)
  const currentLang = params.lang || 'ko'; // 기본값: 한국어
  
  const i18nNavigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      
      console.log('🚀🚀🚀 [useI18nNavigate] 1️⃣ CONTEXT BEFORE NAVIGATION:', {
        '🎯 Target': to,
        '🌐 Current Lang': currentLang,
        '📄 Params State': {
          lang: params.lang,
          allParams: params,
          hasParams: Object.keys(params).length > 0
        },
        '📍 Window Location': {
          pathname: window.location.pathname,
          href: window.location.href
        },
        '🔄 React Router Ready': !!baseNavigate
      });
      
      // 숫자 인자(예: navigate(-1))는 언어 prefix 없이 그대로 전달
      if (typeof to === 'number') {
        console.log('🔢 Numeric navigation (no i18n):', to);
        baseNavigate(to);
        return;
      }

      let finalTo: To;

      if (typeof to === 'string') {
        // 문자열 경로 처리
        
        // 외부 링크는 그대로 사용
        if (to.startsWith('http://') || to.startsWith('https://')) {
          console.log('🌍 External link (no i18n):', to);
          finalTo = to;
        }
        // 이미 언어 prefix가 있으면 그대로 사용
        else if (to.match(/^\/[a-z]{2}\//)) {
          console.log('✅ Already has language prefix:', to);
          finalTo = to;
        }
        // 절대 경로인데 언어 prefix 없음 → 언어 prefix 추가
        else if (to.startsWith('/')) {
          finalTo = `/${currentLang}${to}`;
          console.log(`🔀 Added language prefix: ${to} → ${finalTo}`);
        }
        // 상대 경로는 그대로 사용 (AutoI18nLayout이 처리)
        else {
          console.log('📍 Relative path (AutoI18nLayout will handle):', to);
          finalTo = to;
        }
      } else {
        // Path 객체 처리 (pathname, search, hash 등 포함)
        let pathname = to.pathname ?? '';
        
        // 외부 링크는 그대로 사용
        if (pathname.startsWith('http://') || pathname.startsWith('https://')) {
          console.log('🌍 External link object (no i18n):', pathname);
          finalTo = to;
        }
        // 이미 언어 prefix가 있으면 그대로 사용
        else if (pathname.match(/^\/[a-z]{2}\//)) {
          console.log('✅ Path object already has language prefix:', pathname);
          finalTo = to;
        }
        // 절대 경로인데 언어 prefix 없음 → 언어 prefix 추가
        else if (pathname.startsWith('/')) {
          pathname = `/${currentLang}${pathname}`;
          finalTo = { ...to, pathname };
          console.log(`🔀 Added language prefix to path object: ${to.pathname} → ${pathname}`);
        }
        // 상대 경로는 그대로 사용
        else {
          console.log('📍 Relative path object (AutoI18nLayout will handle):', pathname);
          finalTo = to;
        }
      }
      
      console.log('🚀 Final navigation:', finalTo);
      
      // 🚀 React Router v7 정석: push navigation by default, allow callers to opt into replace if 필요
      console.log('🔄 [React Router v7 정석] Using proper React Router navigation...');
      
      const navigationOptions = {
        preventScrollReset: true, // Keep scroll position stable unless caller overrides
        ...options,
      };
      
      baseNavigate(finalTo, navigationOptions);
      console.log('✅ [useI18nNavigate] 1️⃣ CONTEXT AFTER baseNavigate():', {
        '📍 Final Target': finalTo,
        '🔧 Navigation Options': navigationOptions,
        '⏰ Executed At': new Date().toISOString(),
        '🚀 Method': 'React Router v7 정석 방식'
      });
      
      // 🚨 TEMPORARY DEBUG: Test if window.location works (not proper React Router way)
      console.log('🔥🔥🔥 [EMERGENCY DEBUG] Testing window.location vs baseNavigate');
      console.log('🔥 About to call baseNavigate, then test window.location as backup');
      
      // 🔍 3️⃣ useLocation HOOKS UPDATE 지연 분석 (비동기로 처리)
      setTimeout(() => {
        console.log('✅ [useI18nNavigate] 3️⃣ ASYNC UPDATE CHECK (100ms later):', {
          '🎯 Target': {
            attempted: finalTo,
            expected: typeof finalTo === 'string' ? finalTo : finalTo.pathname
          },
          '📍 Current State': {
            windowPathname: window.location.pathname,
            windowHref: window.location.href
          },
          '🔄 Update Status': {
            pathMatches: window.location.pathname === (typeof finalTo === 'string' ? finalTo : finalTo.pathname),
            timeSinceNavigation: '100ms'
          }
        });
        
        // 🚨 EMERGENCY: If baseNavigate failed to update location context, try window.location
        const expectedPath = typeof finalTo === 'string' ? finalTo : finalTo.pathname;
        if (window.location.pathname !== expectedPath) {
          console.log('🚨🚨🚨 [3️⃣ HOOKS UPDATE DELAY] baseNavigate failed to update! Fallback to window.location...');
          // Use getCleanOrigin() to avoid trailing dot issues (e.g., https://app.ugot.uk. → https://app.ugot.uk)
          window.location.href = getCleanOrigin() + expectedPath + window.location.search;
        } else {
          console.log('✅ [3️⃣ HOOKS SUCCESS] baseNavigate successfully updated location context!');
        }
      }, 100);
    },
    [baseNavigate, currentLang, params]
  );

  return i18nNavigate;
}

/**
 * 기본 내보내기 - 편의성을 위한 alias
 */
export default useI18nNavigate;

/**
 * useNavigate 별칭 - 기존 import를 수정 없이 사용 가능
 * 
 * @example
 * import { useNavigate } from '../hooks/useI18nNavigate';
 * const navigate = useNavigate(); // 자동으로 i18n 처리
 */
export { useI18nNavigate as useNavigate };
